export enum EAcademyChapter {
  Publish = "publish",
  Tenant = "tenant",
  MatchingProperties = "matching_properties",
  Overview = "overview",
  Pipeline = "pipeline",
  Waitlist = "waitlist",
  Visits = "visits",
  AutoMails = "auto_mails",
  VisitMode = "visit_mode",
  Owner = "owner",
  Settings = "settings",
  Faq = "faq",
}

export enum EAcademyMedia {
  AvailabilityCalendar = "availability_calendar",
  BookingSlots = "booking_slots",
  BulkMessage = "bulk_message",
  CandidateDrawer = "candidate_drawer",
  CandidateList = "candidate_list",
  CandidateTabs = "candidate_tabs",
  ContinueModal = "continue_modal",
  ExtraTab = "extra_tab",
  FilterPanel = "filter_panel",
  InvitationMail = "invitation_mail",
  InviteToApply = "invite_to_apply",
  MatchCalculator = "match_calculator",
  MatchingAlerts = "matching_alerts",
  MatchingDetail = "matching_detail",
  MatchingProperties = "matching_properties",
  MyApplications = "my_applications",
  NoShow = "no_show",
  OtherApplications = "other_applications",
  ProfileAgenda = "profile_agenda",
  PropertyCandidatesCard = "property_candidates_card",
  PropertyListBadge = "property_list_badge",
  PropertyPage = "property_page",
  RentmatchSettingsForm = "rentmatch_settings_form",
  RowMenu = "row_menu",
  ScoreSettings = "score_settings",
  ScoreZoom = "score_zoom",
  SendToOwner = "send_to_owner",
  SettingsMails = "settings_mails",
  SettingsTenantProfile = "settings_tenant_profile",
  SlideActivate = "slide_activate",
  SlideAgenda = "slide_agenda",
  SlideBooking = "slide_booking",
  SlideCalendar = "slide_calendar",
  SlideCandidateTabs = "slide_candidate_tabs",
  SlideFilters = "slide_filters",
  SlideIntro = "slide_intro",
  SlideMailEditor = "slide_mail_editor",
  SlideOtherProperties = "slide_other_properties",
  SlideOwner = "slide_owner",
  SlideSameLink = "slide_same_link",
  SlideTips = "slide_tips",
  StepMailEditor = "step_mail_editor",
  VisitCard = "visit_card",
  VisitMode = "visit_mode",
  VisitReminderMail = "visit_reminder_mail",
}

export enum EAcademyMediaType {
  Image = "image",
  Video = "video",
}

export interface IAcademyMedia {
  type: EAcademyMediaType;
  src: string;
  // A still shown before a video starts.
  poster?: string;
  // Captured on a phone: rendered narrower so it does not fill the page.
  isPhone?: boolean;
}

// Every question offers three answers; the number is the right one.
export type TAcademyQuizOption = 1 | 2 | 3;

// Picked answer per question, keyed by `<chapter>.<section>`.
export type TAcademyQuizAnswers = Record<string, TAcademyQuizOption>;

export interface IAcademyScore {
  correct: number;
  // Questions answered so far: the score counts against these, so two right
  // answers out of two read as a good start, not as two out of six.
  answered: number;
  total: number;
}

export interface IAcademySection {
  id: string;
  media?: EAcademyMedia;
  // A check question under the section, with immediate feedback. Nothing is
  // stored: the answer only lives on screen.
  quizAnswer?: TAcademyQuizOption;
  // In a question list: the theme heading this question opens.
  group?: string;
}

export interface IAcademyChapter {
  id: EAcademyChapter;
  // The picture on the chapter's card in the academy overview.
  cover: EAcademyMedia;
  // Sections show as a question list: the title is the question, the body the
  // answer that opens under it.
  isQuestionList?: boolean;
  sections: IAcademySection[];
}

export interface IAcademyStartStepLink {
  id: string;
  // A dashboard screen outside the academy, such as a settings tab.
  path: string;
}

export interface IAcademyStartStep {
  id: string;
  // Where the step is explained in full.
  chapter: EAcademyChapter;
  // Screens where the step is done, linked next to the explanation.
  links?: IAcademyStartStepLink[];
}

export interface IAcademySearchResult {
  id: string;
  title: string;
  // The chapter the result sits in, or the short version.
  context: string;
  snippet: string;
  path: string;
}

export interface IAcademySlide {
  id: string;
  media?: EAcademyMedia;
}

export enum EAcademyBlockType {
  Paragraph = "paragraph",
  Bullets = "bullets",
}

export type TAcademyBlock =
  | { type: EAcademyBlockType.Paragraph; text: string }
  | { type: EAcademyBlockType.Bullets; items: string[] };
